// src/features/auth/index.ts
import apiUtils from '../../../services/api/apiUtils';

// ✅ Login Types
interface LoginData {
  email: string;
  password: string;
}

interface AdminData {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  roleId: number;
  isActive: boolean;
  lastLoginAt: string;
  createdAt: string;
  updatedAt: string;
  role: {
    id: number;
    name: string;
    permissions?: string[];
  };
}

interface TokenData {
  accessToken: string;
  refreshToken: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    admin: AdminData;
    tokens: TokenData;
  };
  meta: {
    timestamp: string;
  };
}

// ✅ Register Types
interface RegisterUserData {
  name: string;
  email: string;
  password: string;
  role: 'admin' | 'superadmin' | 'loading_manager';
}

// ✅ User Data (for listing)
interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt?: string;
  updatedAt?: string;
}

// ✅ LOGIN
export const loginUser = async (credentials: LoginData): Promise<LoginResponse | null> => {
  try {
    const response = await apiUtils.post<{
      admin: AdminData;
      tokens: TokenData;
    }>('/auth/admin/login', credentials);
    
    // Transform ApiResponse to LoginResponse format
    if (response.success && response.data) {
      return {
        success: response.success,
        message: response.message || 'Login successful',
        data: response.data,
        meta: {
          timestamp: response.meta?.timestamp || new Date().toISOString()
        }
      };
    }
    return null;
  } catch (error) {
    console.error('❌ Error logging in:', error);
    return null;
  }
};

// ✅ REGISTER USER
export const registerUser = async (data: RegisterUserData): Promise<User | null> => {
  try {
    const response = await apiUtils.post<User>('/users/register', data);
    return response.data || null;
  } catch (error) {
    console.error('❌ Error registering user:', error);
    return null;
  }
};

// ✅ GET ALL USERS (Admin users)
export const getAllUsers = async (): Promise<User[] | null> => {
  try {
    const response = await apiUtils.get<any>('/admin/users/admins');
    // API may return paginated data with items array
    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }
    if (response.data && response.data.items) {
      return response.data.items;
    }
    return response.data ?? [];
  } catch (error) {
    console.error('Error fetching users:', error);
    return null;
  }
};

// ──────────────────────────────────────────────────────────────────
// Admin-user CRUD wrappers used by the rebuilt UserList page.
// Backend canonical shape is documented in
// BackendAPI/src/api/controllers/admin/user.controller.ts.
// ──────────────────────────────────────────────────────────────────

export interface AdminUserListItem {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  isActive: boolean;
  lastLoginAt?: string | null;
  lastSeenAt?: string | null;
  createdAt: string;
  role?: { id: number; name: string; description?: string | null };
}

export interface AdminUserListResponse {
  items: AdminUserListItem[];
  pagination?: { page: number; limit: number; total: number; totalPages: number };
}

export interface AdminUserListParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string; // role name (server side may filter by role.name) — currently best-effort
  status?: 'active' | 'inactive' | 'all';
  isActive?: boolean;
}

export const getAdminUsers = async (params: AdminUserListParams = {}): Promise<AdminUserListResponse> => {
  const q = new URLSearchParams();
  if (params.page) q.append('page', String(params.page));
  if (params.limit) q.append('limit', String(params.limit));
  if (params.search) q.append('search', params.search);
  if (params.role) q.append('role', params.role);
  if (params.status) q.append('status', params.status);
  if (params.isActive !== undefined) q.append('isActive', String(params.isActive));

  const response = await apiUtils.get<any>(`/admin/users/admins?${q.toString()}`);
  const data = response.data;
  // Backend may ship a paginated envelope {items, pagination} OR a
  // bare array; handle both so old callers don't break.
  if (Array.isArray(data)) {
    return { items: data, pagination: undefined };
  }
  if (data && Array.isArray(data.items)) {
    return { items: data.items, pagination: data.pagination };
  }
  return { items: [], pagination: undefined };
};

export interface AdminUserPayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  password?: string;
  roleId?: number;
  isActive?: boolean;
}

export const createAdminUser = async (payload: AdminUserPayload): Promise<AdminUserListItem | null> => {
  const res = await apiUtils.post<AdminUserListItem>('/admin/users/admins', payload);
  return res.data ?? null;
};

export const updateAdminUser = async (id: number, payload: AdminUserPayload): Promise<AdminUserListItem | null> => {
  const res = await apiUtils.put<AdminUserListItem>(`/admin/users/admins/${id}`, payload);
  return res.data ?? null;
};

export const deleteAdminUser = async (id: number): Promise<void> => {
  await apiUtils.delete(`/admin/users/admins/${id}`);
};
